import React, { useState, useRef } from 'react';
import { useForm } from '../../context/FormContext';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash2, Search, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { isValidUKPostcode } from '../../lib/ukPostcode';
import { titleCaseCompany } from '../../lib/nameCase';

const RESIDENTIAL_STATUSES = [
  { id: 'Homeowner', label: 'Homeowner' },
  { id: 'Tenant - Private', label: 'Tenant - Private' },
  { id: 'Tenant - Housing Association', label: 'Tenant - Housing Assoc.' },
  { id: 'Tenant - Council', label: 'Tenant - Council' },
  { id: 'Living with family', label: 'Living with family' },
  { id: 'Military Accommodation', label: 'Military Accommodation' },
  { id: 'Work Accommodation', label: 'Work Accommodation' },
  { id: 'Student Accommodation', label: 'Student Accommodation' },
  { id: 'Other', label: 'Other' },
];

export const AddressStep = () => {
  const { formData, updateFormData } = useForm();
  const [searchingPostcode, setSearchingPostcode] = useState({});
  const [addressOptions, setAddressOptions] = useState({});
  const [houseTouched, setHouseTouched] = useState({});
  // Track postcodes we've already auto-looked-up so blurring the same value
  // twice doesn't spam the API. Also track lookup failures per index so we
  // can surface an inline "postcode not recognised" warning.
  const autoLookedRef = useRef({});
  const [lookupFailed, setLookupFailed] = useState({});
  // Refs per address card so we can scroll a newly-added entry into view —
  // mirrors the EmploymentDetailsStep fix to stop users adding an empty
  // "Previous Address" card and then getting stuck at validation time.
  const entryRefs = useRef({});
  
  const addresses = formData.addresses || [{ postcode: '', house_number: '', house_name: '', street: '', town: '', county: '', years: 0, months: 0, residential_status: '' }];

  const updateAddress = (index, field, value) => {
    const newAddresses = [...addresses];
    newAddresses[index] = { ...newAddresses[index], [field]: value };
    updateFormData({ addresses: newAddresses });
  };

  const addAddress = () => {
    const newIndex = addresses.length;
    updateFormData({
      addresses: [...addresses, { postcode: '', house_number: '', house_name: '', street: '', town: '', county: '', years: 0, months: 0, residential_status: '' }]
    });
    toast.success('Previous address added — fill in the new entry below, or use Remove if added by mistake.');
    setTimeout(() => {
      const node = entryRefs.current[newIndex];
      if (node && typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const removeAddress = (index) => {
    if (addresses.length > 1) {
      const newAddresses = addresses.filter((_, i) => i !== index);
      updateFormData({ addresses: newAddresses });
      setAddressOptions(prev => {
        const newOptions = { ...prev };
        delete newOptions[index];
        return newOptions;
      });
    }
  };

  const lookupPostcode = async (index, { silent = false } = {}) => {
    const postcode = addresses[index]?.postcode?.trim();
    if (!postcode || postcode.length < 5) {
      if (!silent) toast.error('Please enter a valid postcode');
      return;
    }

    autoLookedRef.current[index] = postcode;
    setSearchingPostcode({ ...searchingPostcode, [index]: true });
    setLookupFailed((prev) => ({ ...prev, [index]: false }));

    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/address-lookup/${encodeURIComponent(postcode)}`);

      if (response.data.success && response.data.addresses?.length > 0) {
        setAddressOptions({ ...addressOptions, [index]: response.data.addresses });
        if (!silent) toast.success(`Found ${response.data.addresses.length} addresses`);
      } else {
        setLookupFailed((prev) => ({ ...prev, [index]: true }));
        // When Royal Mail has terminated the postcode, show the explanatory
        // message for longer and with higher visual weight so the user
        // understands it's not an app bug — they need to supply a different
        // postcode. The backend-provided error text already carries the
        // termination date; we just adjust duration/variant here.
        const errMsg = response.data.error || 'No addresses found for this postcode';
        if (!silent) {
          if (response.data.terminated) {
            toast.error(errMsg, { duration: 10000 });
          } else {
            toast.error(errMsg);
          }
        }
      }
    } catch (error) {
      setLookupFailed((prev) => ({ ...prev, [index]: true }));
      if (!silent) {
        const msg = error.response?.data?.detail || 'Postcode lookup failed. Please check and try again.';
        toast.error(msg);
      }
    } finally {
      setSearchingPostcode({ ...searchingPostcode, [index]: false });
    }
  };

  // Auto-fire lookup on blur when the postcode is structurally valid AND the
  // customer hasn't already clicked the Check button (or already been
  // auto-looked-up) for this exact value. Silent mode: no toasts on success,
  // and no toast on failure either — the inline warning below the postcode
  // input tells the story. Catches the case where a customer types a
  // postcode, doesn't click Check, and moves on — surfacing "postcode not
  // recognised" instead of letting the broker reject the whole submission.
  const handlePostcodeBlur = (index) => {
    const postcode = (addresses[index]?.postcode || '').trim();
    if (!postcode) return;
    if (!isValidUKPostcode(postcode)) return; // structural check will already show
    if (autoLookedRef.current[index] === postcode) return; // don't re-lookup same value
    if (addressOptions[index] && addressOptions[index].length > 0) return; // already have results
    lookupPostcode(index, { silent: true });
  };

  const selectAddress = (index, selectedAddress) => {
    const newAddresses = [...addresses];
    // Royal Mail PAF often returns street/town/county in ALL CAPS.
    // Title-case them on selection so the UI + downstream payloads are clean.
    newAddresses[index] = {
      ...newAddresses[index],
      house_number: selectedAddress.building_number || '',
      house_name: titleCaseCompany(selectedAddress.building_name || ''),
      sub_building: selectedAddress.sub_building || '',
      street: titleCaseCompany(selectedAddress.street || ''),
      town: titleCaseCompany(selectedAddress.town || ''),
      county: titleCaseCompany(selectedAddress.county || ''),
    };
    updateFormData({ addresses: newAddresses });
    setAddressOptions({ ...addressOptions, [index]: null });
    toast.success('Address selected');
  };

  // Auto-fill street when house number changes and we have address data
  const handleHouseNumberChange = (index, value) => {
    updateAddress(index, 'house_number', value);
    
    // If we have address options, try to find matching address
    const options = addressOptions[index];
    if (options && value) {
      const match = options.find(addr => 
        addr.building_number === value || 
        addr.line1?.toLowerCase().startsWith(value.toLowerCase())
      );
      if (match) {
        updateAddress(index, 'street', match.street || '');
        updateAddress(index, 'town', match.town || '');
      }
    }
  };

  // Calculate total years at addresses
  const totalYears = addresses.reduce((sum, addr) => sum + (addr.years || 0) + ((addr.months || 0) / 12), 0);
  const needsMoreHistory = totalYears < 3;

  return (
    <div className="space-y-6" data-testid="address-step">
      <div className="text-center">
        <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Address Details
        </h2>
        <p className="text-slate-600 hidden md:block">We need 3 years of address history</p>
      </div>

      {addresses.map((address, index) => {
        // Flag incomplete additional address entries so users can spot accidental adds.
        // First entry (index 0) is "current address" and is validated separately.
        const isIncompleteExtra = index > 0 && (
          !address.postcode?.trim() ||
          (!address.house_number?.trim() && !address.house_name?.trim()) ||
          !address.street?.trim() ||
          !address.town?.trim() ||
          !address.residential_status ||
          ((address.years || 0) === 0 && (address.months || 0) === 0)
        );
        return (
        <div
          key={index}
          ref={(el) => { entryRefs.current[index] = el; }}
          className={`p-4 border rounded-lg space-y-4 ${isIncompleteExtra ? 'border-amber-400 bg-amber-50/40' : 'border-slate-200'}`}
          data-testid={`address-${index}`}
        >
          <div className="flex justify-between items-center">
            <h3 className="font-medium text-slate-700">
              {index === 0 ? 'Current Address' : `Previous Address ${index}`}
            </h3>
            {index > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => removeAddress(index)}
                className="gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                data-testid={`remove-address-${index}`}
              >
                <Trash2 className="w-4 h-4" />
                Remove
              </Button>
            )}
          </div>

          {isIncompleteExtra && (
            <div
              className="text-xs text-amber-800 bg-amber-100 border border-amber-200 rounded-md px-3 py-2"
              data-testid={`address-${index}-incomplete-hint`}
            >
              This previous address is empty. Either complete it or click <strong>Remove</strong> above if it was added by mistake.
            </div>
          )}

          {/* Postcode Lookup */}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">
              Postcode <span className="text-red-500">*</span>
            </Label>
            <div className="flex gap-2">
              <Input
                placeholder="e.g. SW1A 1AA"
                value={address.postcode}
                onChange={(e) => {
                  updateAddress(index, 'postcode', e.target.value.toUpperCase());
                  // Clear the "not recognised" warning as soon as the user
                  // edits the postcode again — otherwise the stale warning
                  // stays visible while they're mid-fix.
                  setLookupFailed((prev) => ({ ...prev, [index]: false }));
                }}
                onBlur={() => handlePostcodeBlur(index)}
                className="h-11 flex-1"
                autoComplete="postal-code"
                data-testid={`postcode-input-${index}`}
              />
              <Button
                type="button"
                onClick={() => lookupPostcode(index)}
                disabled={searchingPostcode[index]}
                className="h-11 px-4 brand-bg  text-white"
                data-testid={`lookup-postcode-${index}`}
              >
                {searchingPostcode[index] ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-1" />
                    Check
                  </>
                )}
              </Button>
            </div>
            {address.postcode && address.postcode.trim().length >= 3 && !isValidUKPostcode(address.postcode) && (
              <p
                className="mt-1 flex items-center gap-1 text-xs text-amber-700"
                data-testid={`postcode-warning-${index}`}
              >
                <AlertTriangle className="w-3 h-3" /> Doesn&apos;t look like a UK postcode (expected format e.g. SW1A 1AA)
              </p>
            )}
            {/* Auto-lookup failure — postcode looks valid but doesn't exist in
                Ideal Postcodes. Prompts the customer to re-check rather than
                letting a made-up / typo'd postcode reach the broker. */}
            {lookupFailed[index] && isValidUKPostcode(address.postcode) && (
              <div
                className="mt-2 flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-md"
                data-testid={`postcode-not-recognised-${index}`}
              >
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-amber-800 text-sm">
                  <strong>{address.postcode}</strong> isn&apos;t a recognised UK postcode. Please double-check it — a wrong postcode is a common reason for broker rejections.
                </p>
              </div>
            )}
          </div>

          {/* Address Dropdown */}
          {addressOptions[index] && addressOptions[index].length > 0 && (
            <div className="rounded-lg border-2 border-blue-400 bg-blue-50/60 p-3 shadow-sm ring-2 ring-blue-200/70">
              <Label className="text-sm font-semibold text-blue-800 mb-1 flex items-center gap-1.5">
                <Search className="w-3.5 h-3.5" />
                Select your address ({addressOptions[index].length} found)
              </Label>
              <p className="text-xs text-blue-700/80 mb-2">
                Please pick your address from the list to auto-fill the fields below.
              </p>
              <Select onValueChange={(val) => selectAddress(index, addressOptions[index][parseInt(val)])}>
                <SelectTrigger
                  className="h-11 bg-white border-2 border-blue-400 hover:border-blue-500 focus:border-blue-600 focus:ring-2 focus:ring-blue-300"
                  data-testid={`address-select-${index}`}
                >
                  <SelectValue placeholder="Tap to choose your address..." />
                </SelectTrigger>
                <SelectContent>
                  {addressOptions[index].map((addr, i) => (
                    <SelectItem key={i} value={i.toString()}>
                      {addr.full}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">
                Flat / Sub-building
              </Label>
              <Input
                placeholder="e.g. 1/2 or Flat 3 (optional)"
                value={address.sub_building || ''}
                onChange={(e) => updateAddress(index, 'sub_building', e.target.value)}
                className="h-11"
                autoComplete="address-line2"
                data-testid={`sub-building-${index}`}
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">
                House Number {!address.house_name?.trim() && <span className="text-red-500">*</span>}
              </Label>
              <Input
                placeholder="e.g. 10"
                value={address.house_number}
                onChange={(e) => handleHouseNumberChange(index, e.target.value)}
                onBlur={() => setHouseTouched((t) => ({ ...t, [index]: true }))}
                className={`h-11 ${houseTouched[index] && !address.house_number?.trim() && !address.house_name?.trim() ? 'border-red-300' : ''}`}
                autoComplete="address-line1"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">
                House/Building Name {!address.house_number?.trim() && <span className="text-red-500">*</span>}
              </Label>
              <Input
                placeholder={address.house_number?.trim() ? 'Optional (e.g. Rose Cottage)' : 'Required if no number'}
                value={address.house_name}
                onChange={(e) => updateAddress(index, 'house_name', e.target.value)}
                onBlur={() => setHouseTouched((t) => ({ ...t, [index]: true }))}
                className={`h-11 ${houseTouched[index] && !address.house_number?.trim() && !address.house_name?.trim() ? 'border-red-300' : ''}`}
                autoComplete="address-line3"
                data-testid={`house-name-${index}`}
              />
              {houseTouched[index] && !address.house_number?.trim() && !address.house_name?.trim() && (
                <p className="text-xs text-red-500 mt-1">House Number or Name is required</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">
                Street <span className="text-red-500">*</span>
              </Label>
              <Input
                placeholder="Street name"
                value={address.street}
                onChange={(e) => updateAddress(index, 'street', e.target.value)}
                className="h-11"
                autoComplete="address-line1"
                data-testid={`street-${index}`}
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">
                Town/City <span className="text-red-500">*</span>
              </Label>
              <Input
                placeholder="Town or city"
                value={address.town}
                onChange={(e) => updateAddress(index, 'town', e.target.value)}
                className="h-11"
                autoComplete="address-level2"
                data-testid={`town-${index}`}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">
                Years at address <span className="text-red-500">*</span>
              </Label>
              <Select 
                value={String(address.years ?? 0)} 
                onValueChange={(value) => updateAddress(index, 'years', parseInt(value))}
              >
                <SelectTrigger className="h-11" data-testid={`address-years-${index}`}>
                  <SelectValue>{String(address.years ?? 0)}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {[...Array(51)].map((_, i) => (
                    <SelectItem key={i} value={String(i)}>{i}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">
                Months at address <span className="text-red-500">*</span>
              </Label>
              <Select 
                value={String(address.months ?? 0)} 
                onValueChange={(value) => updateAddress(index, 'months', parseInt(value))}
              >
                <SelectTrigger className="h-11" data-testid={`address-months-${index}`}>
                  <SelectValue>{String(address.months ?? 0)}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {[...Array(12)].map((_, i) => (
                    <SelectItem key={i} value={String(i)}>{i}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Residential Status */}
          <div>
            <Label className="text-xs text-slate-500 mb-1 block">
              Residential Status <span className="text-red-500">*</span>
            </Label>
            <Select 
              value={address.residential_status || ''} 
              onValueChange={(value) => updateAddress(index, 'residential_status', value)}
            >
              <SelectTrigger className="h-11" data-testid={`residential-status-${index}`}>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {RESIDENTIAL_STATUSES.map((status) => (
                  <SelectItem key={status.id} value={status.id}>{status.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        );
      })}

      {needsMoreHistory && (
        <div className="text-center">
          <p className="text-sm text-amber-600 mb-3">
            You need {(3 - totalYears).toFixed(1)} more years of address history
          </p>
          <Button
            variant="outline"
            onClick={addAddress}
            className="gap-2"
            data-testid="add-address-btn"
          >
            <Plus className="w-4 h-4" />
            Add Previous Address
          </Button>
        </div>
      )}
    </div>
  );
};

export default AddressStep;
